import { useEffect } from "react";
import { Phone, MessageCircle, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ChatWidget } from "@/components/site/ChatWidget";
import { Reveal } from "@/components/site/Reveal";
import { TELEFONO, TELEFONO_HREF } from "@/data/citta";

export default function GenericPage({ pagina, slug }) {
  useEffect(() => {
    document.title = `${pagina.titolo} — SOS Riparazioni 24`;
    window.scrollTo(0, 0);
  }, [pagina]);

  return (
    <div className="min-h-screen bg-obsidian" data-testid={`generica-${slug}`}>
      <Header />
      <main>
        <section className="relative overflow-hidden px-5 pb-20 pt-40 text-center md:px-8">
          <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-amber-500/10 blur-[130px]" />
          <Reveal>
            <p className="overline-label">{pagina.occhiello}</p>
            <h1 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white text-balance sm:text-5xl lg:text-6xl">
              {pagina.titolo}
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              {pagina.intro}
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a
                href={TELEFONO_HREF}
                data-testid={`generica-${slug}-cta-chiama`}
                className="inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-8 py-4 text-base font-bold text-obsidian transition-all hover:-translate-y-0.5 hover:bg-amber-400"
              >
                <Phone size={18} strokeWidth={2.5} /> Chiama ora · {TELEFONO}
              </a>
              <button
                type="button"
                data-testid={`generica-${slug}-cta-chat`}
                onClick={() => window.dispatchEvent(new CustomEvent("apri-chat"))}
                className="inline-flex items-center gap-2.5 rounded-full border border-slate-600 px-8 py-4 text-base font-semibold text-white transition-all hover:border-amber-400 hover:text-amber-400"
              >
                <MessageCircle size={18} /> Chatta con noi
              </button>
            </div>
          </Reveal>
        </section>

        <section className="mx-auto max-w-7xl px-5 pb-20 md:px-8">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {pagina.blocchi.map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08}>
                <article
                  data-testid={`generica-${slug}-blocco-${i}`}
                  className="h-full rounded-2xl border border-slate-800 bg-slatecard p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/50"
                >
                  <h2 className="font-display text-lg font-semibold text-white">{b.t}</h2>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{b.d}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mx-auto mt-12 max-w-2xl space-y-3">
              {pagina.punti.map((p) => (
                <p
                  key={p}
                  className="flex items-center justify-center gap-2.5 rounded-xl border border-slate-800 bg-slatecard px-5 py-4 text-sm text-slate-200"
                >
                  <CheckCircle2 size={17} className="shrink-0 text-emerald-400" />
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </section>

        <section className="border-t border-slate-800 bg-abyss py-16 text-center">
          <Reveal>
            <h2 className="mx-auto max-w-2xl px-5 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Hai un'emergenza adesso? <span className="text-amber-400">Siamo già pronti.</span>
            </h2>
            <div className="mt-7 flex flex-wrap justify-center gap-4 px-5">
              <a
                href={TELEFONO_HREF}
                data-testid={`generica-${slug}-cta-finale`}
                className="inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-8 py-4 text-base font-bold text-obsidian transition-all hover:-translate-y-0.5 hover:bg-amber-400"
              >
                <Phone size={18} strokeWidth={2.5} /> Chiama ora · {TELEFONO}
              </a>
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
